import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Car, Eye, EyeOff } from 'lucide-react';
import { API } from '../utils/api';

export default function LoginPage() {
  const navigate = useNavigate();
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotDialogOpen, setForgotDialogOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData)
      });
      
      if (!response.ok) {
        throw new Error('Invalid email or password. Please try again.');
      }
      
      const data = await response.json();
      if (data.session_token) {
        localStorage.setItem('session_token', data.session_token);
      }
      toast.success('Login successful');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail) {
      toast.error('Please enter your email');
      return;
    }
    
    setForgotLoading(true);
    try {
      const response = await fetch(`${API}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send reset email');
      }
      
      toast.success('New password sent to your email!');
      setForgotDialogOpen(false);
      setForgotEmail('');
    } catch (error) {
      toast.error(error.message || 'Failed to send reset email');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div 
        className="hidden lg:block relative bg-cover bg-center"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1755817594309-c621aeb19e7d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjYXIlMjB3YXNoJTIwdHVubmVsJTIwYmx1ZSUyMGxpZ2h0fGVufDB8fHx8MTc3MDk2NDM4NHww&ixlib=rb-4.1.0&q=85)' }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-slate-900/40"></div>
        <div className="relative h-full flex flex-col justify-end p-12">
          <div className="flex items-center gap-3 mb-6">
            <Car className="h-10 w-10 text-sky-400" strokeWidth={1.5} />
            <h1 className="text-4xl font-heading font-bold text-white tracking-tight">Happy Day Wash</h1>
          </div>
          <p className="text-xl text-slate-200 max-w-md">Professional fleet vehicle wash management system for modern operations</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12 bg-slate-200">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Car className="h-8 w-8 text-sky-500" strokeWidth={1.5} />
              <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight">Happy Day Wash</h1>
            </div>
          </div>

          <Card className="border border-slate-200 bg-white">
            <form onSubmit={handleLogin} className="p-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="login-email" className="text-sm font-semibold uppercase tracking-wide">Email</Label>
                <Input
                  id="login-email"
                  data-testid="login-email-input"
                  type="email"
                  placeholder="your@email.com"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  required
                  className="bg-slate-50 border-slate-200"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="login-password" className="text-sm font-semibold uppercase tracking-wide">Password</Label>
                <div className="relative">
                  <Input
                    id="login-password"
                    data-testid="login-password-input"
                    type={showPassword ? "text" : "password"}
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    required
                    className="bg-slate-50 border-slate-200 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full uppercase font-bold tracking-wider bg-primary text-primary-foreground hover:bg-primary/90" 
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>
              <button
                type="button"
                onClick={() => setForgotDialogOpen(true)}
                className="w-full text-sm text-slate-500 hover:text-slate-700 mt-2"
                data-testid="forgot-password-link"
              >
                Forgot Password?
              </button>
            </form>
          </Card>

          {/* Forgot Password Dialog */}
          <Dialog open={forgotDialogOpen} onOpenChange={setForgotDialogOpen}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl uppercase">Forgot Password</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-slate-600">
                  Enter your email and we'll send you a new password.
                </p>
                <div className="space-y-2">
                  <Label className="uppercase text-xs font-bold tracking-wide">Email</Label>
                  <Input
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="bg-slate-50 border-slate-200"
                    data-testid="forgot-email-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => setForgotDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    className="flex-1 uppercase font-bold"
                    onClick={handleForgotPassword}
                    disabled={forgotLoading}
                    data-testid="forgot-submit-button"
                  >
                    {forgotLoading ? 'Sending...' : 'Send New Password'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
}